import { PDFDocument, rgb, StandardFonts } from "pdf-lib";

const PAGE_WIDTH = 595;
const PAGE_HEIGHT = 842;
const MARGIN = 50;
const FONT_SIZE = 10;
const LINE_HEIGHT = 14;
const RIGHT_X = PAGE_WIDTH - MARGIN - 200;

function interpolate(text, vars) {
  if (!text) return "";
  return text
    .replace(/\{\{unitNo\}\}/g, vars.unitNo || "«UNIT NO»")
    .replace(/\{\{areaDifference\}\}/g, vars.areaDifference || "«AREA»")
    .replace(/\{\{areaDifferenceInSqFt\}\}/g, vars.areaDifferenceInSqFt || "«AREA SQFT»")
    .replace(/\{\{floorNo\}\}/g, vars.floorNo || "«FLOOR NO»")
    .replace(/\{\{wingName\}\}/g, vars.wingName || "«WING»")
    .replace(/\{\{bsp\}\}/g, vars.bsp || "«BSP»")
    .replace(/\{\{paidAmt\}\}/g, vars.paidAmt || "_________")
    .replace(/\{\{paidDate\}\}/g, vars.paidDate || "_______")
    .replace(/\{\{paidChequeNo\}\}/g, vars.paidChequeNo || "_______")
    .replace(/\{\{parkingfloor\}\}/g, vars.parkingfloor || "")
    .replace(/\{\{parkingNumber\}\}/g, vars.parkingNumber || "______")
    .replace(/\{\{parkingLength\}\}/g, vars.parkingLength || "_______")
    .replace(/\{\{parkingBreadth\}\}/g, vars.parkingBreadth || "__________")
    .replace(/\{\{parkingHeight\}\}/g, vars.parkingHeight || "______");
}

function splitTextIntoLines(text, maxWidth, fontObj, size) {
  const words = text.split(" ");
  const lines = [];
  let current = "";
  words.forEach((word) => {
    const test = current + word + " ";
    if (fontObj.widthOfTextAtSize(test, size) > maxWidth) {
      lines.push(current);
      current = word + " ";
    } else {
      current = test;
    }
  });
  if (current) lines.push(current);
  return lines;
}

export async function generateAllotmentLetterPDFBytes(template, parkingData, allotment) {
  const pdfDoc = await PDFDocument.create();
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const italic = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);

  let page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  let y = PAGE_HEIGHT - 50;

  const newPageIfNeeded = () => {
    if (y < 60) {
      page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
      y = PAGE_HEIGHT - 50;
    }
  };

  const drawText = (text, { boldText = false, size = FONT_SIZE, x = MARGIN } = {}) => {
    page.drawText(text, { x, y, size, font: boldText ? bold : font });
    y -= LINE_HEIGHT;
    newPageIfNeeded();
  };

  const drawParagraph = (text, { boldText = false, x = MARGIN } = {}) => {
    if (!text) return;
    const clean = text.replace(/\n/g, " ");
    const words = clean.split(" ");
    let line = "";
    const maxWidth = PAGE_WIDTH - x - MARGIN;
    const f = boldText ? bold : font;

    words.forEach((word) => {
      const test = line + word + " ";
      if (f.widthOfTextAtSize(test, FONT_SIZE) > maxWidth) {
        page.drawText(line, { x, y, size: FONT_SIZE, font: f });
        y -= LINE_HEIGHT;
        newPageIfNeeded();
        line = word + " ";
      } else {
        line = test;
      }
    });

    if (line) {
      page.drawText(line, { x, y, size: FONT_SIZE, font: f });
      y -= LINE_HEIGHT;
      newPageIfNeeded();
    }
  };

  const drawFooterNote = () => {
    const note = "* This is a system-generated allotment letter; no signature is required.";
    const noteWidth = font.widthOfTextAtSize(note, 8);
    page.drawText(note, {
      x: PAGE_WIDTH - MARGIN - noteWidth,
      y: 30,
      size: 8,
      font,
    });
  };
console.log('parkingData=====>', parkingData)
  const vars = {
    unitNo: parkingData?.unitNo,
    areaDifference: parkingData?.areaDifference,
    areaDifferenceInSqFt: parkingData?.areaDifferenceInSqFt,
    floorNo: parkingData?.floorNo,
    wingName: parkingData?.wingName,
    bsp: parkingData?.unitCost?.bsp,
    paidAmt: parkingData?.paidAmt?.ocr,
    paidDate: parkingData?.paidDate,
    paidChequeNo: parkingData?.paidChequeNo,
    parkingfloor: parkingData?.parkingfloor,
    parkingNumber: parkingData?.parkingNumber,
    parkingLength: parkingData?.parkingLength,
    parkingBreadth: parkingData?.parkingBreadth,
    parkingHeight: parkingData?.parkingHeight,
  };

  const allotmentDate = allotment?.created_at
    ? new Date(allotment.created_at).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "«Date»";

  const coApp = parkingData?.applicants?.[1]?.applicant;
  const coAppName = coApp
    ? [coApp.firstName, coApp.middleName, coApp.lastName].filter(Boolean).join(" ")
    : "";

  // =================== HEADER ===================
  page.drawText(`Allotment No. ${parkingData?.unitNo || "«Primary Unit No»"}`, {
    x: MARGIN,
    y,
    size: FONT_SIZE,
    font: bold,
  });
  page.drawText(`Date: ${allotmentDate}`, {
    x: RIGHT_X,
    y,
    size: FONT_SIZE,
    font: bold,
  });
  y -= 30;

  // =================== APPLICANT BLOCK ===================
  const layout = template.applicant_layout || "standard";

  if (layout === "wingd") {
    // Row 1: "To," | "Pan Card No.:"
    page.drawText("To,", { x: MARGIN, y, size: FONT_SIZE, font: bold });
    page.drawText(`Pan Card No.: ${parkingData?.pan_number?.toUpperCase() || ""}`, {
      x: RIGHT_X, y, size: FONT_SIZE, font: bold,
    });
    y -= LINE_HEIGHT;

    // Row 2: "Name" | "Aadhaar Card No.:"
    page.drawText(parkingData?.name || "", { x: MARGIN, y, size: FONT_SIZE, font: bold });
    page.drawText(`Aadhaar Card No.: ${parkingData?.aadhaar_number || ""}`, {
      x: RIGHT_X, y, size: FONT_SIZE, font: bold,
    });
    y -= 20;

    // Row 3: "Mobile No.:" | "Email ID:"
    page.drawText(`Mobile No.: ${parkingData?.contactNumber || ""}`, {
      x: MARGIN, y, size: FONT_SIZE, font: bold,
    });
    page.drawText(`Email ID: ${parkingData?.email || ""}`, {
      x: RIGHT_X, y, size: FONT_SIZE, font: bold,
    });
    y -= 20;

    // Row 4: Address
    page.drawText(`R/o: ${parkingData?.address || ""}`, {
      x: MARGIN, y, size: FONT_SIZE, font: bold,
    });
    y -= 20;
  } else if (layout === "pnrd") {
    // Row 1: "To," | "Pan Card No.:"
    page.drawText("To,", { x: MARGIN, y, size: FONT_SIZE, font: bold });
    page.drawText(`Pan Card No.: ${parkingData?.pan_number?.toUpperCase() || ""}`, {
      x: RIGHT_X, y, size: FONT_SIZE, font: bold,
    });
    y -= LINE_HEIGHT;

    // Row 2: Name
    page.drawText(parkingData?.name || "", { x: MARGIN, y, size: FONT_SIZE, font: bold });
    y -= LINE_HEIGHT;

    // Row 3: Mobile
    page.drawText(`Mobile No.: ${parkingData?.contactNumber || ""}`, {
      x: MARGIN, y, size: FONT_SIZE, font,
    });
    y -= 20;

    // Row 4: Co-applicant name only (no PAN, no mobile)
    page.drawText(coAppName, { x: MARGIN, y, size: FONT_SIZE, font: bold });
    y -= LINE_HEIGHT;
    y -= 20;

    // Row 5: Email
    page.drawText(`Email ID: ${parkingData?.email || ""}`, {
      x: MARGIN, y, size: FONT_SIZE, font,
    });
    y -= 20;

    // Row 6: Address
    drawParagraph(`R/o: ${parkingData?.address || ""}`);
    y -= 20;
  } else {
    // standard: PPSBR / PKC layout
    // Row 1: "To," | "Pan Card No.:"
    page.drawText("To,", { x: MARGIN, y, size: FONT_SIZE, font: bold });
    page.drawText(`Pan Card No.: ${parkingData?.pan_number?.toUpperCase() || ""}`, {
      x: RIGHT_X, y, size: FONT_SIZE, font: bold,
    });
    y -= LINE_HEIGHT;

    // Row 2: Name
    page.drawText(parkingData?.name || "", { x: MARGIN, y, size: FONT_SIZE, font: bold });
    y -= LINE_HEIGHT;

    // Row 3: Mobile
    page.drawText(`Mobile No.: ${parkingData?.contactNumber || ""}`, {
      x: MARGIN, y, size: FONT_SIZE, font,
    });
    y -= 20;

    // Row 4: Co-applicant name | Co-applicant PAN
    page.drawText(coAppName, { x: MARGIN, y, size: FONT_SIZE, font: bold });
    page.drawText(`Pan Card No.:  ${coApp?.panNumber || ""}`, {
      x: RIGHT_X, y, size: FONT_SIZE, font: bold,
    });
    y -= LINE_HEIGHT;

    // Row 5: Co-applicant mobile
    page.drawText(`Mobile No.: ${coApp?.contactNumber || ""}`, {
      x: MARGIN, y, size: FONT_SIZE, font,
    });
    y -= 20;

    // Row 6: Email
    page.drawText(`Email ID: ${parkingData?.email || ""}`, {
      x: MARGIN, y, size: FONT_SIZE, font,
    });
    y -= 20;

    // Row 7: Address
    drawParagraph(`R/o: ${parkingData?.address || ""}`);
    y -= 20;
  }

  // =================== SUBJECT ===================
  drawParagraph(template.subject || "", { boldText: true });
  y -= 20;
  drawText("Dear Sir/Madam ,", { boldText: true });

  // =================== SECTION COUNTER ===================
  let sectionNum = 1;

  // =================== SECTION 1: ALLOTMENT ===================
  y -= 10;
  page.drawText(`${sectionNum}. Allotment of the said Unit:`, {
    x: MARGIN, y, size: FONT_SIZE, font: bold,
  });
  y -= LINE_HEIGHT;
  sectionNum++;

  drawParagraph(
    template.section1_paragraph
      ? interpolate(template.section1_paragraph, vars)
      : `This has reference to your request referred at the above subject. In that regard, I/we have the pleasure to inform that you have been allotted commercial unit/ premises bearing No. ${parkingData?.unitNo || "«PRIMARY UNIT NO»"} admeasuring RERA Carpet area ${parkingData?.areaDifference || "«AREA»"} sq. mtrs. equivalent to ${parkingData?.areaDifferenceInSqFt || "«SQ FT»"} sq. ft. situated on ${parkingData?.floorNo || "«FLOOR NO»"} floor in Building ${parkingData?.wingName || "«BUILDING»"} in the project known as "${parkingData?.projectName || "«PROJECT NAME»"}", hereinafter referred to as "the said Unit", for a total consideration of Rs. ${parkingData?.unitCost?.bsp || "____________"}/- exclusive of GST, Stamp Duty and Registration Charges.`
  );
  drawFooterNote();

  // =================== SECTION (optional): PARKING ===================
  if (template.has_parking_section) {
    y -= 10;
    page.drawText(`${sectionNum}. Allotment of Garage/ Covered Parking Space(s):`, {
      x: MARGIN, y, size: FONT_SIZE, font: bold,
    });
    y -= LINE_HEIGHT;
    sectionNum++;

    drawParagraph(
      `The Allottee hereby agrees to purchase from the Promoter and the Promoter hereby agrees to allot to the Allottee covered parking space/s, (if applicable) situated at ${parkingData?.parkingfloor || ""} bearing No.${parkingData?.parkingNumber || "______"} admeasuring minimum 9.29 sq. mtrs. having ${parkingData?.parkingLength || "_______"} meters length x ${parkingData?.parkingBreadth || "__________"} meters breadth x ${parkingData?.parkingHeight || "______"} meters vertical clearance being constructed in the project. The Allottee/s hereby confirm and agrees that there may be deviation up to 20 CM from all dimensions of the parking based on the inputs/changes suggested by the structural engineer/ architect.`
    );
    drawParagraph(
      "It is clarified that the parking space areas may contains/includes plumbing, electrical wires, chambers, cutouts etc. and the Allottee shall have no objections for the same."
    );
  }

  // =================== RECEIPT SECTION ===================
  y -= 10;
  page.drawText(`${sectionNum}. Receipt of part consideration:`, {
    x: MARGIN, y, size: FONT_SIZE, font: bold,
  });
  y -= LINE_HEIGHT;
  sectionNum++;

  drawParagraph(
    template.receipt_paragraph
      ? interpolate(template.receipt_paragraph, vars)
      : `We confirmed that we have received from you an amount of Rs.${parkingData?.paidAmt?.ocr || "_________"} /- being 10% of the total consideration value of the said Unit as booking amount/advance payment on ${parkingData?.paidDate || "__________"}, through Cheque/NEFT/RTGS no. ${parkingData?.paidChequeNo || "_________________"}. The above payment received by us have been deposited in RERA Designated Collection Bank Account No. 777705585100, ICICI Bank, Kothrud Branch, Pune having IFS Code No.ICIC0000338. In addition to the above bank account, I/we have opened in the same bank, RERA Designated Separate Bank Account and RERA Designated Transaction Bank Account having Account No.777705585070 and Account No.777705585030 respectively.`
  );

  // =================== DISCLOSURES ===================
  y -= 10;
  page.drawText(`${sectionNum}. Disclosures of information:`, {
    x: MARGIN, y, size: FONT_SIZE, font: bold,
  });
  y -= LINE_HEIGHT;
  sectionNum++;

  drawParagraph("I/We have made available to you the following information namely: -");
  drawParagraph(
    "i) The sanctioned plans, layout plans, along with specifications, approved by the Competent Authority are displayed at the Project site and has also been uploaded on MahaRERA website."
  );
  drawParagraph(
    "ii) The stage wise time Schedule of completion of the Project, including the provisions for civic infrastructure like water, sanitation and electricity is as stated in Annexure – A attached herewith"
  );
  drawParagraph("iii) The Website address of MahaRERA is https://maharera.mahaonline.gov.in/#");

  // =================== ENCUMBRANCES ===================
  y -= 10;
  page.drawText(`${sectionNum}. Encumbrances:`, {
    x: MARGIN, y, size: FONT_SIZE, font: bold,
  });
  y -= LINE_HEIGHT;
  sectionNum++;

  drawParagraph(
    "I/We hereby confirm that the said unit is free from all encumbrances and I/We hereby further confirm that no encumbrances shall be created on the said unit."
  );

  // =================== FURTHER PAYMENTS ===================
  y -= 10;
  page.drawText(`${sectionNum}. Further Payments:`, {
    x: MARGIN, y, size: FONT_SIZE, font: bold,
  });
  y -= LINE_HEIGHT;
  sectionNum++;

  drawParagraph(
    "Further payments towards the consideration of the said unit shall be made by you, in the manner and at the times as well as on the terms and conditions as more specifically enumerated / stated in the Agreement for Sale to be entered into between ourselves and yourselves."
  );

  // =================== POSSESSION ===================
  y -= 10;
  page.drawText(`${sectionNum}. Possession:`, {
    x: MARGIN, y, size: FONT_SIZE, font: bold,
  });
  y -= LINE_HEIGHT;
  sectionNum++;

  const parkingPhrase = template.possession_includes_parking
    ? "along with covered car parking spaces(s) "
    : "";
  drawParagraph(
    `The said unit ${parkingPhrase}shall be handed over to you on or before ${template.possession_date || "«POSSESSION DATE»"} subject to the payment of the consideration amount of the said unit in the manner and at the times as well as per the terms and conditions as more specifically enumerated /stated in the Agreement for Sale to be entered into between ourselves and yourselves.`
  );

  // =================== INTEREST ===================
  y -= 10;
  page.drawText(`${sectionNum}. Interest payment:`, {
    x: MARGIN, y, size: FONT_SIZE, font: bold,
  });
  y -= LINE_HEIGHT;
  sectionNum++;

  drawParagraph(
    "In case of delay in making any payments, you shall be liable to pay interest at the rate which shall be the State Bank of India highest Marginal Cost of Lending Rate Plus Two Percent."
  );

  // =================== CANCELLATION ===================
  y -= 10;
  page.drawText(`${sectionNum}. Cancellation of Allotment:`, {
    x: MARGIN, y, size: FONT_SIZE, font: bold,
  });
  y -= LINE_HEIGHT;
  const cancellationNum = sectionNum;
  sectionNum++;

  drawParagraph(
    "i) In case you desire to cancel the booking an amount mentioned in the Table hereunder written* would be deducted and the balance amount due and payable shall be refunded to you without interest within 45 days from the date of receipt of your letter requesting to cancel the said booking."
  );

  // Cancellation table
  y -= 10;
  const colWidths = [50, 300, 145];
  const rowH = 22;
  let tableX = MARGIN;
  let tableY = y;

  const tableHeaders = [
    "Sr. No.",
    "If the letter requesting to cancel the booking is received",
    "Amount to be deducted*",
  ];
  tableHeaders.forEach((text, i) => {
    page.drawRectangle({
      x: tableX, y: tableY, width: colWidths[i], height: rowH,
      borderWidth: 1, borderColor: rgb(0, 0, 0),
    });
    page.drawText(text, { x: tableX + 4, y: tableY + 6, size: 8, font: bold });
    tableX += colWidths[i];
  });
  tableY -= rowH;

  const tableRows = [
    ["1.", "Within 15 days from issuance of the allotment letter;", "Nil;"],
    ["2.", "Within 16 to 30 days from issuance of the allotment letter;", "1% of the cost of the said unit;"],
    ["3.", "Within 31 to 60 days from issuance of the allotment letter;", "1.5% of the cost of the said unit;"],
    ["4.", "After 61 days from issuance of the allotment letter.", "2% of the cost of the said unit."],
  ];
  tableRows.forEach((row) => {
    let x = MARGIN;
    row.forEach((cell, i) => {
      page.drawRectangle({
        x, y: tableY, width: colWidths[i], height: rowH,
        borderWidth: 1, borderColor: rgb(0, 0, 0),
      });
      page.drawText(cell, { x: x + 4, y: tableY + 6, size: 8, font });
      x += colWidths[i];
    });
    tableY -= rowH;
    if (tableY < 60) {
      page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
      tableY = PAGE_HEIGHT - 50;
    }
  });
  y = tableY - 10;

  drawParagraph(
    "* The amount deducted shall not exceed the amount paid by you towards consideration of the said unit as mentioned in the table above."
  );
  drawParagraph(
    `ii) In the event the amount due and payable referred in Clause ${cancellationNum} (i) above is not refunded within 45 days from the date of receipt of your letter requesting to cancel the said booking, you shall be entitled to receive the balance amount with interest calculated at the rate which shall be the State Bank of India highest Marginal Cost of Lending Rate Plus Two Percent.`
  );

  // =================== OTHER PAYMENTS ===================
  y -= 10;
  page.drawText(`${sectionNum}. Other Payments:`, {
    x: MARGIN, y, size: FONT_SIZE, font: bold,
  });
  y -= LINE_HEIGHT;
  sectionNum++;

  drawParagraph(
    "You shall make the payment of GST, Stamp Duty and Registration Charges, as applicable and such other payments as more specifically mentioned in the Agreement for Sale, the Proforma whereof is enclosed herewith in terms of Clause 10 hereunder written."
  );

  // =================== PROFORMA ===================
  y -= 10;
  page.drawText(`${sectionNum}. Proforma of the Agreement for Sale and binding effect:`, {
    x: MARGIN, y, size: FONT_SIZE, font: bold,
  });
  y -= LINE_HEIGHT;
  const proformaNum = sectionNum;
  sectionNum++;

  drawParagraph(
    `The Proforma of the Agreement for Sale to be entered into between ourselves and yourselves is enclosed herewith for your ready reference. Forwarding the Proforma of the Agreement for Sale does not create a binding obligation on the part of ourselves and yourselves until compliance by yourselves of the mandate as stated in Clause ${sectionNum}.`
  );

  // =================== AGREEMENT ===================
  y -= 10;
  page.drawText(`${sectionNum}. Execution and Registration of the Agreement for Sale:`, {
    x: MARGIN, y, size: FONT_SIZE, font: bold,
  });
  y -= LINE_HEIGHT;
  const agreementNum = sectionNum;
  sectionNum++;

  drawParagraph(
    "i) You shall execute the Agreement for Sale and appear for registration of the same before the concerned Sub-Registrar within a period of 7 days from the date of issuance of this letter or within such period as may be communicated to you. The said period of 7 days can be further extended on our mutual understanding."
  );
  y -= 5;

  page.drawText("•", { x: MARGIN, y, size: FONT_SIZE, font });
  drawFooterNote();
  drawParagraph(
    "In the event the booking is collected in stages and if allottee fails to pay the subsequent stage installment the promoter shall serve upon the allottee to pay the subsequent stage installment within days which if not complied, the promoter shall be entitled to cancel this allotment letter. On such cancellation of the allotment letter, the promoter shall be entitled to forfeit the amount paid by the allottee or such amount as mentioned in the table enumerated in clause 8 whichever is less. In no event to amount to be forfeited shall exceed the amount mentioned in the above referred table. Except for the above all the terms and conditions as enumerated in this allotment letter shall be applicable even for cases where booking amount is collected in stages.",
    { x: MARGIN + 12 }
  );

  y -= 10;
  drawParagraph(
    "ii) If you fail to execute the Agreement for Sale and appear for registration of the same before the concerned Sub-Registrar within the stipulated period 7 days from the date of issuance of this letter or within such period as may be communicated to you, I/We shall be entitled to serve upon you a notice calling upon you to execute the Agreement for Sale and appear for registration of the same within 15 (Fifteen) days, which if not complied, I/We shall be entitled to cancel this allotment letter and further I/We shall be entitled to forfeit an amount not exceeding 2% of the cost of the said unit and the balance amount if any due and payable shall be refunded without interest within 45 days from the date of expiry of the notice period."
  );
  y -= 10;
  drawParagraph(
    `iii) In the event the balance amount due and payable referred in Clause ${agreementNum} (ii) above is not refunded within 45 days from the date of expiry of the notice period, you shall be entitled to receive the balance amount with interest calculated at the rate which shall be the State Bank of India highest Marginal Cost of Lending Rate plus two percent.`
  );

  // =================== VALIDITY ===================
  y -= 10;
  page.drawText(`${sectionNum}. Validity of allotment letter:`, {
    x: MARGIN, y, size: FONT_SIZE, font: bold,
  });
  y -= LINE_HEIGHT;
  sectionNum++;

  drawParagraph(
    "This allotment letter shall not be construed to limit your rights and interest upon execution and registration of the Agreement for Sale between ourselves and yourselves. Cancellation of allotment of the said unit thereafter, shall be covered by the terms and conditions of the said registered document."
  );

  // =================== HEADINGS ===================
  y -= 10;
  page.drawText(`${sectionNum}. Headings:`, {
    x: MARGIN, y, size: FONT_SIZE, font: bold,
  });
  y -= LINE_HEIGHT;

  drawParagraph(
    "Headings are inserted for convenience only and shall not affect the construction of the various Clauses of this allotment letter."
  );

  // =================== SIGNATORY ===================
  if (template.signatory_enabled) {
    y -= 50;
    page.drawText(template.signatory_company || "", { x: MARGIN, y, size: FONT_SIZE, font: bold });
    y -= 30;
    page.drawText("Authorized Signatory", { x: MARGIN, y, size: FONT_SIZE, font });
    y -= LINE_HEIGHT;
    page.drawText(
      `(Name: ${template.signatory_name || ""})                                Email Id: ${template.signatory_email || ""}`,
      { x: MARGIN, y, size: FONT_SIZE, font }
    );
    y -= LINE_HEIGHT;
    page.drawText("Place: Pune", { x: MARGIN, y, size: FONT_SIZE, font });
  }

  // =================== ACKNOWLEDGEMENT ===================
  y -= 30;
  const ackText = "CONFIRMATION & ACKNOWLEDGEMENT";
  const ackWidth = italic.widthOfTextAtSize(ackText, FONT_SIZE);
  page.drawText(ackText, {
    x: (PAGE_WIDTH - ackWidth) / 2,
    y,
    size: FONT_SIZE,
    font: bold,
  });
  page.drawLine({
    start: { x: (PAGE_WIDTH - ackWidth) / 2, y: y - 2 },
    end: { x: (PAGE_WIDTH - ackWidth) / 2 + ackWidth, y: y - 2 },
    thickness: 1,
    color: rgb(0, 0, 0),
  });
  y -= 20;

  drawParagraph(
    "I/We have read and understood the contents of this allotment letter and the Annexure. I/We hereby agree and accept the terms and conditions as stipulated in this allotment letter."
  );
  y -= 35;

  if (template.acknowledgement_show_date) {
    page.drawText(
      `Signature: _______________                                                                                                       Date : ${new Date().toLocaleDateString()}  `,
      { x: MARGIN, y, size: FONT_SIZE, font }
    );
    y -= LINE_HEIGHT;
  } else {
    page.drawText("Signature: _______________", { x: MARGIN, y, size: FONT_SIZE, font });
    y -= LINE_HEIGHT;
  }

  page.drawText(`Name: ${parkingData?.name || ""} (Allottee/s)`, {
    x: MARGIN, y, size: FONT_SIZE, font,
  });
  y -= LINE_HEIGHT;

  page.drawText(`${coAppName} (Allottee/s)`, {
    x: MARGIN, y, size: FONT_SIZE, font,
  });
  y -= LINE_HEIGHT;

  if (template.acknowledgement_show_date) {
    page.drawText("Place: Pune", { x: MARGIN, y, size: FONT_SIZE, font });
    y -= LINE_HEIGHT;
  }

  drawFooterNote();

  // =================== ANNEXURE A ===================
  if (template.has_annexure_a) {
    page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
    y = PAGE_HEIGHT - 70;

    const annexATitle = "ANNEXURE – A";
    page.drawText(annexATitle, {
      x: (PAGE_WIDTH - bold.widthOfTextAtSize(annexATitle, FONT_SIZE + 2)) / 2,
      y,
      size: FONT_SIZE + 2,
      font: bold,
    });
    y -= 20;

    const annexASubTitle = "Stage wise time Schedule of Completion of the Project –";
    page.drawText(annexASubTitle, {
      x: (PAGE_WIDTH - bold.widthOfTextAtSize(annexASubTitle, FONT_SIZE)) / 2,
      y,
      size: FONT_SIZE,
      font: bold,
    });
    y -= 70;

    const annexColWidths = [30, 380, 95];
    const annexRowHeight = 48;
    let annexX = MARGIN;
    let annexY = y;

    ["Sr. No.", "Stages", "Date of Completion"].forEach((text, i) => {
      page.drawRectangle({
        x: annexX, y: annexY, width: annexColWidths[i], height: annexRowHeight,
        borderWidth: 1, borderColor: rgb(0, 0, 0),
      });
      page.drawText(text, { x: annexX + 4, y: annexY + 6, size: 7.5, font: bold });
      annexX += annexColWidths[i];
    });
    annexY -= annexRowHeight;

    const annexRows = [
      ["1.", "Excavation", ""],
      ["2.", "Basements (if any)", ""],
      ["3.", "Podiums (if any)", ""],
      ["4.", "Plinth", ""],
      ["5.", "Stilt (if any)", ""],
      ["6.", "Slabs of Super Structure", ""],
      ["7.", "Internal Walls, Internal Plaster, Completion of Floorings, Doors and Windows.", ""],
      ["8.", "Sanitary Electrical and Water Supply Fittings within the said units.", ""],
      ["9.", "Staircase, Lifts Wells and Lobbies at each Floor Level Overhead and Underground Water Tanks.", ""],
      ["10.", "External Plumbing and External Plaster, Elevation, Completion of Terraces with Waterproofing.", ""],
      ["11.", "Installation of Lifts, Water Pumps, Firefighting, Fittings and Equipment, Electrical Fittings, Mechanical Equipment, Finishing to Entrance Lobby/s, Plinth Protection, Paving of Areas, Appurtenant to Building / Wing, Compound Wall and all other requirements as may be required to complete Project as per specifications in Agreement of Sale, Other Activities.", ""],
      ["12.", "Internal Roads & Footpaths, Lighting.", ""],
      ["13.", "Water Supply", ""],
      ["14.", "Sewerage (Chamber, Lines, Septic Tank, STP)", ""],
      ["15.", "Storm Water Drains", ""],
      ["16.", "Treatment and disposal of Sewage and Sullage a Water", ""],
      ["17.", "Solid Waste Management & disposal", ""],
      ["18.", "Water Conservation / Rain Water Harvesting.", ""],
      ["19.", "Electrical Meter Room, Sub-Station, Receiving Station.", ""],
      ["20.", "Others", ""],
    ];

    annexRows.forEach((row) => {
      const isRow11 = row[0] === "11.";
      let rh = annexRowHeight;
      let wrappedLines = [];

      if (isRow11) {
        wrappedLines = splitTextIntoLines(row[1], annexColWidths[1] - 8, font, 8);
        rh = wrappedLines.length * 10 + 8;
      }

      let x = MARGIN;
      row.forEach((cell, i) => {
        page.drawRectangle({
          x, y: annexY, width: annexColWidths[i], height: rh,
          borderWidth: 1, borderColor: rgb(0, 0, 0),
        });
        if (isRow11 && i === 1) {
          wrappedLines.forEach((line, idx) => {
            page.drawText(line.trim(), {
              x: x + 4, y: annexY + rh - 12 - idx * 10, size: 8, font,
            });
          });
        } else {
          page.drawText(cell, { x: x + 4, y: annexY + 6, size: 8, font });
        }
        x += annexColWidths[i];
      });

      annexY -= rh;
      if (annexY < 60) {
        page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
        annexY = PAGE_HEIGHT - 70;
      }
    });

    y = annexY - 10;

    if (template.signatory_enabled) {
      y -= 50;
      page.drawText(template.signatory_company || "", { x: MARGIN, y, size: FONT_SIZE, font: bold });
      y -= 30;
      page.drawText("Authorized Signatory", { x: MARGIN, y, size: FONT_SIZE, font });
      y -= LINE_HEIGHT;
    }

    drawFooterNote();
  }

  return await pdfDoc.save();
}
